import { getToken } from '../../components/helpers/TokenHelpers';
import { isObjectNull } from '../../components/helpers/ObjectHelpers';
import pRetry from 'p-retry';

const BaseApiRepository = {

    async optionsAsync(method = 'GET') {
        const token = await this.baseGetToken();
        const options = {
            method: method,
            headers: {
                'Accept': 'application/json',
                'Authorization': token
            }
        };

        switch (method) {
            case 'PATCH':
                options.headers['Content-Type'] = 'application/json-patch+json';
                break;
            default:
                options.headers['Content-Type'] = 'application/json';
                break;
        }

        return options;
    },

    async postOptionsAsync(obj) {
        const options = await this.optionsAsync('POST');
        if (!isObjectNull(obj)) {
            options.body = JSON.stringify(obj);
        }
        return options;
    },

    async deleteOptionsAsync(obj) {
        const options = await this.optionsAsync('DELETE');
        if (!isObjectNull(obj)) {
            options.body = JSON.stringify(obj);
        }
        return options;
    },

    async putOptionsAsync(obj) {
        const options = await this.optionsAsync('PUT');
        if (!isObjectNull(obj)) {
            options.body = JSON.stringify(obj);
        }
        return options;
    },

    async patchOptionsAsync(obj) {
        const options = await this.optionsAsync('PATCH');
        options.body = JSON.stringify(obj);

        return options;
    },

    async getAsync(url, abortSignal) {
        const options = await this.optionsAsync();
        const response = await this.fetchAsync(url, options, abortSignal);
        return response;
    },

    async postAsync(url, obj, abortSignal) {
        const options = await this.postOptionsAsync(obj);
        const response = await this.fetchAsync(url, options, abortSignal);

        return response;
    },

    async deleteAsync(url, obj) {
        const options = await this.deleteOptionsAsync(obj);
        const response = await this.fetchAsync(url, options);

        return response;
    },

    async putAsync(url, obj) {
        const options = await this.putOptionsAsync(obj);
        const response = await this.fetchAsync(url, options);

        return response;
    },

    async patchAsync(url, obj) {
        const options = await this.patchOptionsAsync(obj);
        const response = await this.fetchAsync(url, options);

        return response;
    },

    async patchWithEtagAsync(url, obj, etag) {
        const options = await this.patchOptionsAsync(obj);
        this.appendEtagHeader(options, etag);
        const response = await this.fetchAsync(url, options);

        return response;
    },

    async baseGetToken() {
        const searchParams = new URLSearchParams(window.location.search);
        if (!searchParams.has("token")) {
            return getToken();
        }
        return searchParams.get("token");
    },

    async fetchAsync(url, options, abortSignal = null) {
        const fetchCall = async () => {
            const fetchOptions = abortSignal 
                ? { ...options, signal: abortSignal }
                : options;

            const response = await fetch(`api/${url}`, fetchOptions);

            // Retry on server errors (5xx)
            if (!response.ok && response.status >= 500) {
                throw new Error(response.statusText);
            }
            return response;
        }

        try {
            return await pRetry(fetchCall, {
                retries: 3,
                factor: 3,
                minTimeout: 1000,
                maxTimeout: 10000,
                shouldRetry: (error) => {
                    if (error.name === 'AbortError') return false;
                    if (!error.response) return false;
                    if (error.response.url.includes('api/source/config')) return false;
                    return true;
                },
                onFailedAttempt: error => {
                    console.warn(error, `StatusCode: ${error.status} - Fetch attempt ${error.attemptNumber} failed. There are ${error.retriesLeft} retries left.`);
                }
            });
        } catch (error) {
            if (error.name === 'AbortError') {
                return {
                    ok: false,
                    aborted: true
                };
            }

            console.warn(error);
            return {
                ok: false,
                status: 503,
            }
        }
    },

     async putFormDataAsync(url, formData) {
        const token = await this.baseGetToken();
        const response = await fetch(`api/${url}`, {
            method: 'PUT',
            headers: {
                'Authorization': token,
                // Do NOT set Content-Type - browser will set it automatically with boundary
            },
            body: formData
        });
        
        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        return response.json().catch(() => null); // Handle empty responses
    },

    appendEtagHeader(options, eTag) {
        if (eTag) {
            options.headers['If-Match'] = eTag;
        }
    }
};

export default BaseApiRepository;
